import { LowerCasePipe, NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { AudioTrackingComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/audio-tracking/audio-tracking.component';
import { IKpiCard } from '@cta-analytics-details/cta-analytics-details-kpis-tile/cta-analytics-details-kpi-card/cta-analytics-details-kpi-card.interface';
import { AnalyticsDetailsTableComponent } from '@cta-analytics-dialog/deliverable-analytics-content/analytics-details-table/analytics-details-table.component';
import { DeliverableFindingsStore } from '@cta-analytics-dialog/deliverable-analytics-content/deliverable-findings.store';
import { TimelineGraphComponent } from '@cta-analytics-dialog/deliverable-analytics-content/graphs/timeline-graph/timeline-graph.component';
import { ITimelineGraphInputData } from '@cta-analytics-dialog/deliverable-analytics-content/graphs/timeline-graph/timeline-graph.interface';
import { EDeliverableType } from '@enums/campaign.enum';
import { IAudioTrackingAnalytics } from '@interfaces/deliverable-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { VideoPlayerComponent } from '@shared/components/video-player/video-player.component';
import { AnalyticsDetailsUtilitiesService } from '@utilities/analytics-details-utilities.service';
import { DurationUtilitiesService } from '@utilities/duration-utilities.service';
import { FindingVisualizerService } from '@utilities/finding-visualizer.service';
import { EChartsOption } from 'echarts';
import { MultiMediaPostAnalyticsBaseDirective } from '../multi-media-post-analytics-base/multi-media-post-analytics-base.directive';
import { IAudioTrackingPostAnalyticsTableData } from './audio-tracking-post-analytics.interface';

@Component({
  imports: [
    NgTemplateOutlet,
    TranslateModule,
    TimelineGraphComponent,
    AnalyticsDetailsTableComponent,
    MatTableModule,
    MatSortModule,
    MatButtonModule,
    VideoPlayerComponent,
    AudioTrackingComponent,
    DotsSpinnerComponent,
    IconComponent,
    LowerCasePipe,
    MatMenuModule,
  ],
  providers: [DeliverableFindingsStore],
  selector: 'app-audio-tracking-post-analytics',
  templateUrl: './audio-tracking-post-analytics.component.html',
})
export class AudioTrackingPostAnalyticsComponent extends MultiMediaPostAnalyticsBaseDirective<
  IAudioTrackingPostAnalyticsTableData,
  IAudioTrackingAnalytics
> {
  public override readonly displayedColumns: string[] = ['timestamp', 'title', 'actions'];
  public override readonly dataSource: MatTableDataSource<IAudioTrackingPostAnalyticsTableData> =
    new MatTableDataSource();
  public override readonly deliverableType = EDeliverableType.audioTracking;

  protected override readonly currentDeliverable = computed(() => {
    const deliverableId = this.deliverableId();
    const executionAnalytics = this.executionAnalyticsWithFindings();

    if (deliverableId && executionAnalytics) {
      return executionAnalytics?.ctaAnalytics?.audioTrackings?.find((at) => at.deliverableId === deliverableId);
    }

    return null;
  });

  public override readonly kpiCardData: IKpiCard = {
    icon: 'view.svg',
    id: 'audioTracking-count',
    title: this.translate.instant('pages.campaignDetails.ctaAnalytics.details.audioTracking.totalCount'),
    value: 0,
  };
  public override readonly chartConfiguration = signal<Partial<EChartsOption>>({
    title: {
      text: this.translate.instant('pages.campaignDetails.ctaAnalytics.details.audioTracking.audioTrackingOverTime'),
    },
  });

  public readonly videoMedia = computed(() => {
    return this.media().filter((m) => m.isVideo);
  });

  private readonly findingVisualizerService = inject(FindingVisualizerService);

  protected override prepareChartAndTableData(): void {
    const dotGraphData: ITimelineGraphInputData[] = [];
    const keywords = this.currentDeliverable()?.keywords ?? [];
    const removedIds = this.manualFindingStatusStore?.serverRemovedIds() ?? new Set<string>();
    const visualizers = this.findingVisualizerService.buildFromKeywords(keywords, removedIds);

    const tableData: IAudioTrackingPostAnalyticsTableData[] = visualizers.map((visualizer) => {
      const [primaryFinding] = visualizer.findings;
      const aggregatedVideoTime = this.calculateAggregatedVideoTime(
        primaryFinding.mediaIndex,
        primaryFinding.videoTime,
      );

      const kwAnalytics = keywords.find((k) => k.keywordId === primaryFinding.parentId);

      dotGraphData.push({
        id: visualizer.id,
        mediaIndex: primaryFinding.mediaIndex,
        status: kwAnalytics?.status ?? primaryFinding.status,
        videoTime: primaryFinding.videoTime,
        xAxisValue: aggregatedVideoTime,
      });

      return {
        ...visualizer,
        aggregatedVideoTime,
        mediaIndex: primaryFinding.mediaIndex ?? 0,
        text: primaryFinding.audio,
        timestamp: DurationUtilitiesService.convertSecondsToVideoTime(aggregatedVideoTime, true),
        videoTime: primaryFinding.videoTime ?? 0,
      };
    });

    const sortedTableData = [...tableData].sort((a, b) => a.aggregatedVideoTime - b.aggregatedVideoTime);

    this.dataSource.data = sortedTableData;
    this.dataCount.set(sortedTableData.length);
    this.dotGraphData.set([...dotGraphData].sort((a, b) => a.xAxisValue - b.xAxisValue));
    this.setKeywordTableRowIds(this.findingVisualizerService.buildKeywordRowIdMap(sortedTableData));
  }

  protected override setCounts(): void {
    const audioTrackingAnalytics = this.currentDeliverable();

    const keywords = audioTrackingAnalytics?.keywords ?? [];
    const keywordNames = keywords.map((k) => k.keyword);
    const findings = keywords.flatMap((k) => k.findings ?? []);
    const uniqueFindings = AnalyticsDetailsUtilitiesService.getUniqueTextFindings(findings) as string[];

    const counts = AnalyticsDetailsUtilitiesService.getIndividualKeywordCounts(keywordNames, uniqueFindings);
    const total = Object.values(counts).reduce((sum, val) => sum + val, 0);
    this.kpiCardData.value = total;

    AnalyticsDetailsUtilitiesService.mapKeywordCountsToDeliverableForm(this.deliverableForm(), counts);
  }

  protected override setKeywordList(): void {
    const audioTrackingAnalytics = this.currentDeliverable();

    if (audioTrackingAnalytics?.keywords && audioTrackingAnalytics.keywords.length > 0) {
      const { keywordList, kpiCardList } = AnalyticsDetailsUtilitiesService.buildKeywordLists(
        audioTrackingAnalytics.keywords,
        'keyword',
        'findings',
        'equalizer.svg',
      );
      this.keywordList.set(keywordList);
      this.keywordCardList.set(kpiCardList);
    }
  }

  protected override getKeywords(): string[] {
    return this.currentDeliverable()?.keywords?.map((k) => k.keyword) ?? [];
  }
}
